import { formatCurrency } from '@angular/common';

import {
  RackeoComparisonPriceRow,
  RackeoPlanContract,
  RackeoPlanContractNormalized,
} from './services/locale.service';

const COMPARISON_PLAN_KEYS = ['plan1', 'plan2', 'plan3'] as const;

export function formatRackeoAmount(
  value: number | null | undefined,
  priceLocale: string,
  decimals = 0
): string {
  const safe = value ?? 0;
  const fractionDigits = Math.max(0, Math.trunc(decimals));

  return formatCurrency(safe, priceLocale, '', undefined, `1.${fractionDigits}-${fractionDigits}`).trim();
}

export function perYearFromContractTotal(total: number, years: number): number {
  if (years <= 0) {
    return total;
  }

  return Math.round(total / years);
}

export function normalizePlanContract(contract: RackeoPlanContract): RackeoPlanContractNormalized {
  return {
    ...contract,
    perYear: perYearFromContractTotal(contract.total, contract.years),
  };
}

export function normalizePlanContracts<T extends { contracts: RackeoPlanContract[] }>(
  plan: T
): Omit<T, 'contracts'> & { contracts: RackeoPlanContractNormalized[] } {
  return {
    ...plan,
    contracts: plan.contracts.map(normalizePlanContract),
  };
}

export function normalizeHostingPlans<T extends { contracts: RackeoPlanContract[] }>(
  plans: T[]
): Array<Omit<T, 'contracts'> & { contracts: RackeoPlanContractNormalized[] }> {
  return plans.map(normalizePlanContracts);
}

function contractRowLabel(years: number): string {
  return years === 1 ? 'Contrato por 1 Año' : `Contrato por ${years} Años`;
}

export function buildHostingComparisonPrices(
  plans: Array<{ contracts: RackeoPlanContractNormalized[] }>,
  priceLocale: string
): RackeoComparisonPriceRow[] {
  const contractCount = plans.reduce(
    (max, plan) => Math.max(max, plan.contracts?.length ?? 0),
    0
  );

  return Array.from({ length: contractCount }, (_, index) => {
    const years = plans.find((plan) => plan.contracts[index])?.contracts[index]?.years ?? index + 1;
    const row: RackeoComparisonPriceRow = {
      label: contractRowLabel(years),
      plan1: '',
      plan2: '',
      plan3: '',
    };

    COMPARISON_PLAN_KEYS.forEach((key, planIndex) => {
      row[key] = formatRackeoAmount(plans[planIndex]?.contracts[index]?.total, priceLocale);
    });

    return row;
  });
}

export function prepareWebHostingPlansData<T extends { contracts: RackeoPlanContract[] }>(
  plans: T[],
  priceLocale: string
): {
  plans: Array<Omit<T, 'contracts'> & { contracts: RackeoPlanContractNormalized[] }>;
  comparisonPrices: RackeoComparisonPriceRow[];
} {
  const normalizedPlans = normalizeHostingPlans(plans);

  return {
    plans: normalizedPlans,
    comparisonPrices: buildHostingComparisonPrices(normalizedPlans, priceLocale),
  };
}